import { useCallback } from "react";
import { QueryClient, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { QUERY_STALE_TIME, QUERY_GC_TIME } from "@/lib/queryConfig";
import { userQueryKeys } from "@/lib/queryKeys";
import { logger } from '@/lib/logger';

interface VaultProfileData {
  id?: string;
  government_id_url?: string | null;
  proof_of_address_url?: string | null;
  vaccination_records_url?: string | null;
  created_at?: string;
}

interface ShareRecord {
  id: string;
  created_at: string;
  status: string;
  organization_id: string;
  submissions: {
    form_id: string;
  } | null;
}

interface ApplicationListItem {
  id: string;
  created_at: string;
  status?: string;
  type?: "form_response";
  applicant_name?: string;
}

async function fetchVaultDocuments(userId: string) {
  try {
    const [profileResult, sharesResult] = await Promise.all([
      supabase
        .rpc('get_profile_secure', { target_user_id: userId })
        .maybeSingle(),
      supabase
        .from('shares')
        .select(`
          id,
          created_at,
          status,
          organization_id,
          submissions!inner(form_id)
        `)
        .eq('submissions.user_id', userId)
    ]);

    if (sharesResult.error) {
      console.warn('[prefetch] Failed to fetch shares:', sharesResult.error);
    }

    return {
      profile: profileResult.data as VaultProfileData | null,
      shares: (sharesResult.data as unknown as ShareRecord[] | null) || []
    };
  } catch (error) {
    logger.error('[prefetch] fetchVaultDocuments exception:', error);
    return { profile: null, shares: [] };
  }
}

async function fetchFormResponses(userId: string) {
  try {
    // Use the same RPC as UserDashboard to avoid cache key conflicts
    const { data, error } = await supabase.rpc('get_my_form_responses', {
      p_limit: 20
    });

    if (error) {
      console.warn('[prefetch] Failed to fetch form responses:', error);
      return [];
    }

    return data || [];
  } catch (error) {
    logger.error('[prefetch] fetchFormResponses exception:', error);
    return [];
  }
}

async function fetchFormInvitations(userId: string) {
  try {
    const { data, error } = await supabase
      .from('form_invitations')
      .select(`
        id,
        status,
        created_at,
        due_date,
        subject,
        message,
        form_template_id,
        form_templates(title, description)
      `)
      .eq('recipient_user_id', userId)
      .order('created_at', { ascending: false });

    if (error) {
      console.warn('[prefetch] Failed to fetch invitations:', error);
      return [];
    }

    return data || [];
  } catch (error) {
    logger.error('[prefetch] fetchFormInvitations exception:', error);
    return [];
  }
}

async function fetchFamilyMembers(userId: string) {
  try {
    const { data, error } = await supabase
      .from('family_members')
      .select('id, full_name, relationship, created_at')
      .eq('user_id', userId)
      .order('created_at', { ascending: false });

    if (error) {
      console.warn('[prefetch] Failed to fetch family members:', error);
      return [];
    }

    return data || [];
  } catch (error) {
    logger.error('[prefetch] fetchFamilyMembers exception:', error);
    return [];
  }
}

async function safePrefetch<T>(
  queryClient: QueryClient, 
  key: readonly unknown[], 
  fn: () => Promise<T>,
  options?: { staleTime?: number; gcTime?: number }
) {
  try {
    await queryClient.prefetchQuery({
      queryKey: key,
      queryFn: fn,
      staleTime: options?.staleTime ?? QUERY_STALE_TIME.DYNAMIC,
      gcTime: options?.gcTime ?? QUERY_GC_TIME.MEDIUM,
    });
  } catch (error) {
    logger.error("[prefetch] Failed to prefetch query", key, error);
  }
}

export async function prefetchVaultData(queryClient: QueryClient, userId: string) {
  await safePrefetch(
    queryClient,
    userQueryKeys.vaultDocuments(userId),
    () => fetchVaultDocuments(userId),
    { staleTime: QUERY_STALE_TIME.DYNAMIC, gcTime: QUERY_GC_TIME.MEDIUM }
  );
}

export async function prefetchProfileDashboardData(queryClient: QueryClient, userId: string) {
  await Promise.all([
    safePrefetch(
      queryClient,
      userQueryKeys.savedForms(userId),
      () => fetchFormResponses(userId),
      { staleTime: QUERY_STALE_TIME.DYNAMIC, gcTime: QUERY_GC_TIME.MEDIUM }
    ),
    safePrefetch(
      queryClient,
      userQueryKeys.formInvitations(userId),
      () => fetchFormInvitations(userId),
      { staleTime: QUERY_STALE_TIME.SEMI_STATIC, gcTime: QUERY_GC_TIME.MEDIUM }
    ),
    safePrefetch(
      queryClient,
      userQueryKeys.familyMembers(userId),
      () => fetchFamilyMembers(userId),
      { staleTime: QUERY_STALE_TIME.STATIC, gcTime: QUERY_GC_TIME.LONG }
    ),
  ]);
}

const normalizePath = (path: string) => path.split("?")[0];
const PROFILE_ROUTE_WHITELIST = /^\/profile\/(dashboard|upload|vault|settings|forms)(?:\/|$)/;

export function useProfileRoutePrefetch() {
  const queryClient = useQueryClient();

  const prefetchRoute = useCallback(
    (path: string, userId?: string) => {
      if (!userId) return;

      const normalized = normalizePath(path);

      if (!normalized.startsWith("/profile/")) {
        return;
      }

      if (!PROFILE_ROUTE_WHITELIST.test(normalized)) {
        return;
      }

      const tasks: Promise<unknown>[] = [];

      if (normalized.startsWith("/profile/dashboard")) {
        tasks.push(prefetchProfileDashboardData(queryClient, userId));
      }

      if (normalized.startsWith("/profile/vault")) {
        tasks.push(prefetchVaultData(queryClient, userId));
      }

      if (tasks.length > 0) {
        void Promise.all(tasks);
      }
    },
    [queryClient]
  );

  return { prefetchRoute };
}
